<!-- src/apps/secret/components/conceal/landing/SpecsSection.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';

  import LandingSectionHeader from './LandingSectionHeader.vue';

  const { t } = useI18n();

  // Security specs. Every code/claim pair matches what this deployment really
  // does: E2EE file attachments (client-side, key only in the link), server-side
  // AES at rest for text, atomic single read, native TTL, bcrypt'd passphrase,
  // manual burn, consent-first analytics.
  //
  // CONSENT_FIRST replaces the former NO_TRACKING claim: the service now offers
  // consent-gated Google Analytics, so an absolute "no third-party tracking"
  // line would be false. The honest claim is that nothing loads until you agree
  // and refusing is one click — see PrivacyView + the cookie-consent banner.
  // E2EE_FILES is the newest, strongest claim: file attachments are encrypted in
  // the browser (key only in the link fragment), so the server truly cannot read
  // them — genuine end-to-end, unlike the server-side-at-rest text path.
  const specs = [
    { code: 'E2EE_FILES', titleKey: 'landing.specs.attach_title', descKey: 'landing.specs.attach_desc', featured: true },
    { code: 'AES-256', titleKey: 'landing.specs.cipher_title', descKey: 'landing.specs.cipher_desc', featured: false },
    { code: 'ATOMIC_READ', titleKey: 'landing.specs.read_title', descKey: 'landing.specs.read_desc', featured: false },
    { code: 'NATIVE_TTL', titleKey: 'landing.specs.ttl_title', descKey: 'landing.specs.ttl_desc', featured: false },
    { code: 'BCRYPT', titleKey: 'landing.specs.passphrase_title', descKey: 'landing.specs.passphrase_desc', featured: false },
    { code: 'BURN()', titleKey: 'landing.specs.burn_title', descKey: 'landing.specs.burn_desc', featured: false },
    { code: 'CONSENT_FIRST', titleKey: 'landing.specs.tracking_title', descKey: 'landing.specs.tracking_desc', featured: false },
    { code: 'LGPD', titleKey: 'landing.specs.compliance_title', descKey: 'landing.specs.compliance_desc', featured: false },
  ];

  // A 2-column spec sheet (lighter than a grid of boxes). Right divider only when
  // the next row exists in the same column pair; bottom divider except on the
  // last row — clean for any count (here 7).
  const COLS = 2;
  const hasRightNeighbor = (i: number) => (i + 1) % COLS !== 0 && i + 1 < specs.length;
  const lastRowStart = specs.length - (specs.length % COLS || COLS);
</script>

<template>
  <section class="border-b border-day-line dark:border-white/[0.07]">
    <LandingSectionHeader
      number="03."
      :title="t('landing.specs.title')" />
    <div class="grid grid-cols-1 md:grid-cols-2">
      <div
        v-for="(spec, i) in specs"
        :key="spec.code"
        class="grid grid-cols-[8rem_1fr] items-start gap-3 p-5 transition-colors duration-200 sm:px-8"
        :class="[
          spec.featured
            ? 'shadow-[inset_3px_0_0_0_var(--color-brand-500)] dark:shadow-[inset_3px_0_0_0_var(--color-brand-400)]'
            : 'hover:bg-day-100 dark:hover:bg-night-100',
          hasRightNeighbor(i) ? 'md:border-r md:border-day-line md:dark:border-white/[0.07]' : '',
          i < specs.length - 1 ? 'border-b border-day-line dark:border-white/[0.07]' : '',
          i >= lastRowStart ? 'md:border-b-0' : '',
        ]">
        <!-- code chip — fixed-width column so every title lines up -->
        <span
          class="mt-0.5 self-start justify-self-start rounded border px-2 py-1 font-mono text-[10px] font-semibold tracking-[0.06em] whitespace-nowrap uppercase"
          :class="
            spec.featured
              ? 'border-brand-500/40 bg-brand-500/10 text-brand-700 dark:border-brand-400/40 dark:text-brand-300'
              : 'border-day-line bg-day-100 text-brand-600 dark:border-white/[0.08] dark:bg-night-200 dark:text-brand-400'
          ">
          {{ spec.code }}
        </span>
        <div class="min-w-0">
          <h3 class="font-sans text-[15px] font-semibold tracking-[-0.01em] text-day-ink dark:text-night-ink">
            <span class="align-middle">{{ t(spec.titleKey) }}</span>
            <span
              v-if="spec.featured"
              class="ml-2 inline-block rounded-[4px] border border-brand-300 bg-brand-100/70 px-1.5 py-px align-middle font-mono text-[9px] leading-none font-bold tracking-[0.06em] text-brand-700 uppercase dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-300">
              novo
            </span>
          </h3>
          <p class="mt-1 text-[13.5px] leading-[1.6] text-day-ink-muted dark:text-night-ink-muted">
            {{ t(spec.descKey) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
